import {
  appearanceSchemeRoleTokens,
  buildRuntimeRoleTokens,
  parseBrandHex,
  parseRuntimeBrandSeeds,
  RUNTIME_SEED_KEYS,
  type RuntimeBrandSeeds,
  type RuntimeSeedKey,
} from '../runtime/emitRuntimeBrandCss';
import { getAppearanceScheme, isNamedBrand, type NamedBrand } from './defaultAppearance';
import {
  EDITABLE_TOKEN_BY_KEY,
  RADIUS_TOKEN_DEFAULTS,
  SIZE_TOKEN_DEFAULTS,
  TYPE_TOKEN_DEFAULTS,
  isEditableTokenKey,
  type EditableTokenDef,
} from '../catalog/tokenCatalog';
import {
  isComponentSizeKey,
  NXT_DARK_SELECTOR,
  NXT_LIGHT_SELECTOR,
  validateContrast,
} from '../generator/emitCss';
import { hexToRgb } from '../generator/oklch';
import { parseFontFamily } from '../runtime/emitRuntimeTypeCss';
import { defaultSeeds } from '../seeds/defaultSeeds';
import type { SeedConfig, TokenMap } from '../generator/types';

export type TokenOverrideSlot = 'light' | 'dark' | 'shared';

export type AppearanceTokenOverrides = {
  light?: TokenMap;
  dark?: TokenMap;
  shared?: TokenMap;
};

export const RADIUS_FACTOR_MIN = 0;
export const RADIUS_FACTOR_MAX = 2;
export const RADIUS_FACTOR_STEP = 0.125;
export const RADIUS_FACTOR_PRESETS = [
  { id: 'square', factor: 0 },
  { id: 'soft', factor: 0.5 },
  { id: 'default', factor: 1 },
  { id: 'round', factor: 1.5 },
  { id: 'pill', factor: 2 },
] as const;
export const RADIUS_SCALED_KEYS = ['radius-sm', 'radius-md', 'radius-lg', 'radius-xl'] as const;

const VALUE_MAX = 160;
const RGB = /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+\s*)?\)$/i;
const OKLCH = /^oklch\([^;{}]+\)$/i;
const RADIUS = /^\d+(\.\d+)?(px|em|rem|%)$/;
const SIZE = /^(var\(--nxt-[a-z0-9-]+\)|calc\([^;{}]+\)|\d+(\.\d+)?(px|em|rem|%))$/;
const EM_VALUE = /^(\d+(?:\.\d+)?)em$/;

function formatEm(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return `${Object.is(rounded, -0) ? 0 : rounded}em`;
}

export function radiusOverridesForFactor(factor: number): TokenMap {
  const clamped = Math.min(
    RADIUS_FACTOR_MAX,
    Math.max(RADIUS_FACTOR_MIN, Number.isFinite(factor) ? factor : RADIUS_FACTOR_MIN),
  );
  const scaled = Object.fromEntries(
    RADIUS_SCALED_KEYS.map((key) => {
      const defaultValue = RADIUS_TOKEN_DEFAULTS[key];
      const match = defaultValue ? EM_VALUE.exec(defaultValue) : null;
      if (!match) throw new Error(`nx-tokens: ${key} must have an em default`);
      return [key, formatEm(Number(match[1]) * clamped)];
    }),
  );
  // Pills/chips/toggles use --nxt-radius-full. "Square" (factor 0) means
  // square everywhere, so the pill radius collapses too; any other factor keeps
  // the pill fully round (a slightly softer pill is still a pill) — no override.
  return clamped <= 0.0005 ? { ...scaled, 'radius-full': '0px' } : scaled;
}

/** Apply a corner-radius factor to a token override set: factor 1 releases all
 *  radius overrides; "square" (0) also pins --nxt-radius-full to 0; any other
 *  factor scales the ramp and releases the pill radius. Single source for the
 *  Schema Designer and the settings store. */
export function applyRadiusFactor(
  tokens: AppearanceTokenOverrides | undefined,
  factor: number,
): AppearanceTokenOverrides | undefined {
  let next = tokens;
  if (Math.abs(factor - 1) < 0.0005) {
    for (const key of RADIUS_SCALED_KEYS) next = writeOverride(next, key, 'shared', null);
    return writeOverride(next, 'radius-full', 'shared', null);
  }
  const overrides = radiusOverridesForFactor(factor);
  for (const [key, value] of Object.entries(overrides)) next = writeOverride(next, key, 'shared', value);
  if (!('radius-full' in overrides)) next = writeOverride(next, 'radius-full', 'shared', null);
  return next;
}

export function radiusFactorOf(shared: TokenMap | undefined): number | null {
  if (!shared) return null;
  const factors: number[] = [];
  for (const key of RADIUS_SCALED_KEYS) {
    const value = shared[key];
    if (value == null) continue;
    const match = EM_VALUE.exec(value.trim());
    if (!match) return null;
    const defaultValue = RADIUS_TOKEN_DEFAULTS[key];
    const defaultMatch = defaultValue ? EM_VALUE.exec(defaultValue) : null;
    if (!defaultMatch) throw new Error(`nx-tokens: ${key} must have an em default`);
    factors.push(Number(match[1]) / Number(defaultMatch[1]));
  }
  const pillSquare = shared['radius-full'] != null && /^0(px|em|rem)?$/.test(shared['radius-full'].trim());
  if (!factors.length) return pillSquare ? 0 : null;
  const factor = factors[0];
  if (!factors.every((value) => Math.abs(value - factor) <= 0.001)) return null;
  const rounded = Math.round(factor * 1000) / 1000;
  // square pins the pill radius as well; a squared pill with a non-zero ramp
  // (or vice versa) is a hand-edited mix, not a slider position
  if (pillSquare !== (rounded === 0)) return null;
  return rounded;
}

export function parseTokenColor(value: string): string | null {
  const hex = parseBrandHex(value);
  if (hex) return hex;
  const v = value.trim();
  if (!v || v.length > VALUE_MAX || /[;{}]/.test(v)) return null;
  if (v === 'transparent' || v === 'currentColor') return v;
  if (RGB.test(v) || OKLCH.test(v)) return v;
  return null;
}

export function parseRadiusValue(value: string): string | null {
  const v = value.trim();
  if (!v || v.length > VALUE_MAX) return null;
  return RADIUS.test(v) ? v : null;
}

export function parseSizeValue(value: string): string | null {
  const v = value.trim();
  if (!v || v.length > VALUE_MAX || /[;{}]/.test(v)) return null;
  return SIZE.test(v) ? v : null;
}

export function parseTokenFont(value: string): string | null {
  return parseFontFamily(value);
}

export function parseEditableTokenValue(def: EditableTokenDef, value: string): string | null {
  if (def.kind === 'radius') return parseRadiusValue(value);
  if (def.kind === 'size') return parseSizeValue(value);
  if (def.kind === 'font') return parseTokenFont(value);
  return parseTokenColor(value);
}

function parseSlotMap(raw: unknown, slot: TokenOverrideSlot): TokenMap | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: TokenMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string' || !isEditableTokenKey(key)) continue;
    const def = EDITABLE_TOKEN_BY_KEY.get(key);
    if (!def) continue;
    if (slot === 'shared' && def.modeSensitive) continue;
    if (slot !== 'shared' && !def.modeSensitive) continue;
    const parsed = parseEditableTokenValue(def, value);
    if (!parsed) continue;
    out[key] = parsed;
  }
  return Object.keys(out).length ? out : undefined;
}

export function parseAppearanceTokenOverrides(raw: unknown): AppearanceTokenOverrides | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const rec = raw as Record<string, unknown>;
  const light = parseSlotMap(rec.light, 'light');
  const dark = parseSlotMap(rec.dark, 'dark');
  const shared = parseSlotMap(rec.shared, 'shared');
  if (!light && !dark && !shared) return undefined;
  return { ...(light ? { light } : {}), ...(dark ? { dark } : {}), ...(shared ? { shared } : {}) };
}

export function compactTokenOverrides(
  tokens: AppearanceTokenOverrides | undefined,
): AppearanceTokenOverrides | undefined {
  if (!tokens) return undefined;
  return parseAppearanceTokenOverrides(tokens);
}

export function tokenOverrideCount(tokens: AppearanceTokenOverrides | undefined): number {
  if (!tokens) return 0;
  return (
    Object.keys(tokens.light ?? {}).length +
    Object.keys(tokens.dark ?? {}).length +
    Object.keys(tokens.shared ?? {}).length
  );
}

export function hasColorOverrides(tokens: AppearanceTokenOverrides | undefined): boolean {
  return Boolean(tokens?.light || tokens?.dark);
}

/**
 * Same seeds? A missing `info` or `accent` mirrors the brand (that is what the
 * ramp does with it), so `{brand: X}` equals `{brand: X, info: X, accent: X}`.
 */
export function seedsEqual(a: RuntimeBrandSeeds, b: RuntimeBrandSeeds): boolean {
  const hex = (seeds: RuntimeBrandSeeds, key: RuntimeSeedKey) =>
    parseBrandHex((key === 'info' || key === 'accent' ? (seeds[key] ?? seeds.brand) : seeds[key]) ?? '') ?? '';
  return RUNTIME_SEED_KEYS.every((key) => hex(a, key) === hex(b, key));
}

export function parseSourceScheme(raw: unknown): NamedBrand | undefined {
  return typeof raw === 'string' && isNamedBrand(raw) ? raw : undefined;
}

export function generatedRoleMaps(
  seeds: RuntimeBrandSeeds,
  sourceScheme?: NamedBrand,
  base: SeedConfig = defaultSeeds,
): { light: TokenMap; dark: TokenMap } {
  if (sourceScheme) {
    const scheme = getAppearanceScheme(sourceScheme);
    if (scheme && seedsEqual(seeds, scheme.seeds)) {
      return appearanceSchemeRoleTokens(scheme, base);
    }
  }
  return buildRuntimeRoleTokens(seeds, base);
}

export function effectiveRoleMaps(
  seeds: RuntimeBrandSeeds,
  tokens: AppearanceTokenOverrides | undefined,
  sourceScheme?: NamedBrand,
  base: SeedConfig = defaultSeeds,
): { light: TokenMap; dark: TokenMap } {
  const generated = generatedRoleMaps(seeds, sourceScheme, base);
  return {
    light: { ...generated.light, ...tokens?.light },
    dark: { ...generated.dark, ...tokens?.dark },
  };
}

export function generatedSharedTokens(): TokenMap {
  return { ...RADIUS_TOKEN_DEFAULTS, ...TYPE_TOKEN_DEFAULTS, ...SIZE_TOKEN_DEFAULTS };
}

export function effectiveSharedTokens(tokens: AppearanceTokenOverrides | undefined): TokenMap {
  return { ...RADIUS_TOKEN_DEFAULTS, ...tokens?.shared };
}

export function slotForToken(def: EditableTokenDef, theme: 'light' | 'dark'): TokenOverrideSlot {
  return def.modeSensitive ? theme : 'shared';
}

export function readOverride(
  tokens: AppearanceTokenOverrides | undefined,
  key: string,
  slot: TokenOverrideSlot,
): string | undefined {
  return tokens?.[slot]?.[key];
}

export function writeOverride(
  tokens: AppearanceTokenOverrides | undefined,
  key: string,
  slot: TokenOverrideSlot,
  value: string | null,
): AppearanceTokenOverrides | undefined {
  const def = EDITABLE_TOKEN_BY_KEY.get(key);
  if (!def) return compactTokenOverrides(tokens);
  const next: AppearanceTokenOverrides = {
    light: { ...tokens?.light },
    dark: { ...tokens?.dark },
    shared: { ...tokens?.shared },
  };
  const bucket = { ...next[slot] };
  if (value == null || value === '') {
    delete bucket[key];
  } else {
    const parsed = parseEditableTokenValue(def, value);
    if (!parsed) return compactTokenOverrides(tokens);
    bucket[key] = parsed;
  }
  next[slot] = bucket;
  return compactTokenOverrides(next);
}

export function resolveSourceScheme(
  seeds: RuntimeBrandSeeds,
  sourceScheme?: NamedBrand,
): NamedBrand | undefined {
  if (!sourceScheme) return undefined;
  const scheme = getAppearanceScheme(sourceScheme);
  if (!scheme || !seedsEqual(seeds, scheme.seeds)) return undefined;
  return sourceScheme;
}

function renderBlock(selector: string, tokens: TokenMap): string {
  const lines = Object.entries(tokens)
    .map(([k, v]) => `  --nxt-${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${lines}\n}\n`;
}

function assertContrast(label: string, roles: TokenMap): void {
  const issues = validateContrast(roles);
  if (issues.length) {
    const detail = issues.map((i) => `${i.role} (${i.ratio}:1)`).join(', ');
    throw new Error(`nx-tokens: ${label} on-fill contrast below AA 4.5 for: ${detail}`);
  }
}

export function emitAppearanceDocumentCss(doc: {
  seeds: RuntimeBrandSeeds;
  tokens?: AppearanceTokenOverrides;
  sourceScheme?: NamedBrand;
  base?: SeedConfig;
}): string {
  const base = doc.base ?? defaultSeeds;
  const sourceScheme = resolveSourceScheme(doc.seeds, doc.sourceScheme);
  const roles = effectiveRoleMaps(doc.seeds, doc.tokens, sourceScheme, base);
  if (!hasColorOverrides(doc.tokens)) {
    assertContrast('runtime light', roles.light);
    assertContrast('runtime dark', roles.dark);
  }
  const sharedAll = doc.tokens?.shared ?? {};
  // Font-size steps + component size tokens are declared per scale scope (`:root, [data-nx-scope]` in
  // base.css) as multiples of --nxt-size-unit, so an override must be emitted
  // the same way or it would only apply outside the scopes / ignore the scale.
  const shared: TokenMap = {};
  const scoped: TokenMap = {};
  for (const [key, value] of Object.entries(sharedAll)) {
    if (key.startsWith('font-size-') || isComponentSizeKey(key)) scoped[key] = scopeSizeValue(value);
    else shared[key] = value;
  }
  // In @layer nxt.runtime like emitRuntimeRoleCss: beats the generated
  // defaults (nxt.tokens), yields to the apps and the kit's design axes
  // (nxt.app) — see layers.css.
  return (
    `/* runtime brand — @layer nxt.runtime: beats nxt.tokens, yields to nxt.app */\n` +
    `@layer nxt.runtime {\n` +
    renderBlock(NXT_LIGHT_SELECTOR, { ...roles.light, ...shared }) +
    '\n' +
    renderBlock(NXT_DARK_SELECTOR, { ...roles.dark, ...shared }) +
    (Object.keys(scoped).length ? '\n' + renderBlock(':root, [data-nx-scope]', scoped) : '') +
    `}\n`
  );
}

/** `1.125rem` → `calc(1.125 * var(--nxt-size-unit))`; anything else verbatim. */
export function scopeSizeValue(value: string): string {
  const m = /^\s*([0-9]*\.?[0-9]+)rem\s*$/.exec(value);
  if (!m) return value;
  return m[1] === '1' ? 'var(--nxt-size-unit)' : `calc(${m[1]} * var(--nxt-size-unit))`;
}

export function emitAppearanceDocumentLegacyCss(doc: {
  seeds: RuntimeBrandSeeds;
  tokens?: AppearanceTokenOverrides;
  sourceScheme?: NamedBrand;
  base?: SeedConfig;
}): string {
  const brandBg = effectiveRoleMaps(
    doc.seeds,
    doc.tokens,
    resolveSourceScheme(doc.seeds, doc.sourceScheme),
    doc.base ?? defaultSeeds,
  ).light['brand-bg'];
  const hex = parseBrandHex(brandBg);
  if (!hex) {
    throw new Error('nx-tokens: runtime brand-bg must be #rgb or #rrggbb hex');
  }
  const [r, g, b] = hexToRgb(hex);
  return (
    `/* legacy --nx-brand-rgb so leftover --nx-primary follows the live fill */\n` +
    `:root {\n  --nx-brand-rgb: ${r}, ${g}, ${b};\n}\n`
  );
}
